'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useComplaintContact } from '@/src/modules/chat/hooks/use-complaint-contact';
import { useComplaintConversation } from '@/src/modules/chat/hooks/use-complaint-conversation';
import { useComplaintMessages } from '@/src/modules/chat/hooks/use-complaint-messages';
import { useCreateSelfMedicationConversation } from '@/src/modules/chat/hooks/use-create-self-medication-conversation';
import { useMarketplaceCustomerContact } from '@/src/modules/chat/hooks/use-marketplace-customer-contact';
import { useMarketplaceCustomerConversations } from '@/src/modules/chat/hooks/use-marketplace-customer-conversations';
import { useMarketplaceCustomerMessages } from '@/src/modules/chat/hooks/use-marketplace-customer-messages';
import { useSelfMedicationContact } from '@/src/modules/chat/hooks/use-self-medication-contact';
import { useSelfMedicationConversation } from '@/src/modules/chat/hooks/use-self-medication-conversation';
import { useSelfMedicationMessages } from '@/src/modules/chat/hooks/use-self-medication-messages';
import { MChatMessage } from '@/src/modules/chat/models/chat-message';
import { ApplicationError } from '@/src/shared/errors';
import { translateWithFallback } from '@/src/shared/utils/i18n';
import { MarketplaceCustomerChatContext } from '../../../_contexts';
import { EChatTab, TChatTab } from '../../../_enums/chat-tab';
import {
  EChatTabTarget,
  TChatTabTarget,
} from '../../../_enums/chat-tab-target';

interface IMarketplaceCustomerChatProviderProps
  extends React.PropsWithChildren {
  branchId: number | null;
  initialOpenedTab: TChatTab | null;
  initialOpenedTabTarget: TChatTabTarget | null;
}

export function MarketplaceCustomerChatProvider({
  branchId,
  initialOpenedTab,
  initialOpenedTabTarget,
  children,
}: IMarketplaceCustomerChatProviderProps) {
  const apiErrorTranslator = useTranslations('api-errors');
  const commonErrorTranslator = useTranslations('common-errors');

  const router = useRouter();
  const searchParams = useSearchParams();

  const user = useAuthStore((state) => state.user);
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(null);

  const selectedBranchId = branchId;
  const setSelectedBranchId = (branchId: number | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (branchId) {
      params.set('branch_id', branchId.toString());
    } else {
      params.delete('branch_id');
    }
    router.replace(`?${params.toString()}`);
  };

  const currentOpenedTab = initialOpenedTab || EChatTab.WithSeller;
  const setCurrentOpenedTab = (tab: TChatTab) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', tab);
    router.replace(`?${params.toString()}`);
  };

  const {
    data: selfMedicationConversation,
    isLoading: isGettingSelfMedicationConversation,
  } = useSelfMedicationConversation({
    userId: user?.props.id ?? null,
  });
  const {
    data: createdSelfMedicationConversation,
    isLoading: isCreatingSelfMedicationConversation,
    trigger: createSelfMedicationConversation,
  } = useCreateSelfMedicationConversation({
    userId: user?.props.id ?? null,
  });
  const activeSelfMedicationConversation =
    selfMedicationConversation || createdSelfMedicationConversation || null;
  const selfMedicationConversationIsSelected =
    selectedConversationId === activeSelfMedicationConversation?.props.id;
  const {
    data: selfMedicationConversationContact,
    isLoading: isGettingSelfMedicationConversationContact,
  } = useSelfMedicationContact({
    userId: user && selfMedicationConversationIsSelected ? user.props.id : null,
  });
  const {
    messages: selfMedicationConversationMessages,
    isLoading: isGettingSelfMedicationConversationMessages,
    isLoadingMore: isGettingMoreSelfMedicationConversationMessages,
    isReachingEnd: isReachingEndSelfMedicationConversationMessages,
    addMessage: addSelfMedicationConversationMessages,
    loadMore: loadMoreSelfMedicationConversationMessages,
  } = useSelfMedicationMessages({
    userId: user && selfMedicationConversationIsSelected ? user.props.id : null,
  });

  const {
    data: complaintConversation,
    isLoading: isGettingComplaintConversation,
  } = useComplaintConversation({
    userId: user?.props.id ?? null,
  });
  const complaintConversationIsSelected =
    selectedConversationId === complaintConversation?.props.id;
  const {
    data: complaintConversationContact,
    isLoading: isGettingComplaintConversationContact,
  } = useComplaintContact({
    userId: user && complaintConversationIsSelected ? user.props.id : null,
  });
  const {
    messages: complaintConversationMessages,
    isLoading: isGettingComplaintConversationMessages,
    isLoadingMore: isGettingMoreComplaintConversationMessages,
    isReachingEnd: isReachingEndComplaintConversationMessages,
    addMessage: addComplaintConversationMessages,
    loadMore: loadMoreComplaintConversationMessages,
  } = useComplaintMessages({
    userId: user && complaintConversationIsSelected ? user.props.id : null,
  });

  const customerConversationIsSelected =
    !selfMedicationConversationIsSelected && !complaintConversationIsSelected;

  const {
    conversations: sellerConversations,
    isLoading: isGettingSellerConversations,
    isLoadingMore: isGettingMoreSellerConversations,
    isReachingEnd: isReachingEndSellerConversations,
    loadMore: loadMoreSellerConversations,
    refresh: refreshSellerConversations,
    isRefreshing: isRefreshingSellerConversations,
  } = useMarketplaceCustomerConversations({
    userId: user?.props.id ?? null,
    branchId: selectedBranchId,
  });
  const {
    messages: sellerConversationMessages,
    isLoading: isGettingSellerConversationMessages,
    isLoadingMore: isGettingMoreSellerConversationMessages,
    isReachingEnd: isReachingEndSellerConversationMessages,
    addMessage: addSellerConversationMessages,
    loadMore: loadMoreConversationMessages,
  } = useMarketplaceCustomerMessages({
    userId: user?.props.id ?? null,
    conversationId: customerConversationIsSelected
      ? selectedConversationId
      : null,
  });
  const {
    data: sellerConversationContact,
    isLoading: isGettingSellerConversationContact,
  } = useMarketplaceCustomerContact({
    userId: user?.props.id ?? null,
    conversationId: customerConversationIsSelected
      ? selectedConversationId
      : null,
  });

  const getCurrentConversationContact = () => {
    if (selfMedicationConversationIsSelected) {
      return selfMedicationConversationContact || null;
    }
    if (complaintConversationIsSelected) {
      return complaintConversationContact || null;
    }
    return sellerConversationContact || null;
  };
  const isGettingConversations =
    isGettingSellerConversations ||
    isGettingSelfMedicationConversation ||
    isGettingComplaintConversation;
  const isGettingMoreConversations = isGettingMoreSellerConversations || false;
  const isReachingEndConversations = isReachingEndSellerConversations || false;
  const loadMoreConversations = loadMoreSellerConversations;
  const refreshConversations = async () => {
    try {
      await refreshSellerConversations();
    } catch (error) {
      if (error instanceof ApplicationError) {
        notifications.show({
          title: error.name,
          message: translateWithFallback(
            error.message,
            apiErrorTranslator,
            commonErrorTranslator
          ),
          color: 'red',
        });
      }

      throw error;
    }
  };
  const isRefreshingConversations = isRefreshingSellerConversations;
  const getCurrentConversationMessages = () => {
    if (selfMedicationConversationIsSelected) {
      return selfMedicationConversationMessages;
    }
    if (complaintConversationIsSelected) {
      return complaintConversationMessages;
    }
    return sellerConversationMessages;
  };
  const isGettingCurrentConversationContact =
    isGettingSelfMedicationConversationContact ||
    isGettingSellerConversationContact ||
    isGettingComplaintConversationContact;
  const isGettingCurrentConversationMessages =
    isGettingSelfMedicationConversationMessages ||
    isGettingComplaintConversationMessages ||
    isGettingSellerConversationMessages;
  const isGettingMoreCurrentConversationMessages =
    isGettingMoreSelfMedicationConversationMessages ||
    isGettingMoreSellerConversationMessages ||
    isGettingMoreComplaintConversationMessages ||
    false;
  const isReachingEndCurrentConversationMessages =
    isReachingEndSelfMedicationConversationMessages ||
    isReachingEndSellerConversationMessages ||
    isReachingEndComplaintConversationMessages ||
    false;
  const addCurrentConversationMessages = (message: MChatMessage) => {
    if (selfMedicationConversationIsSelected) {
      return addSelfMedicationConversationMessages(message);
    }
    if (complaintConversationIsSelected) {
      return addComplaintConversationMessages(message);
    }
    return addSellerConversationMessages(message);
  };
  const loadMoreCurrentConversationMessages = async () => {
    if (selfMedicationConversationIsSelected) {
      return await loadMoreSelfMedicationConversationMessages();
    }
    if (complaintConversationIsSelected) {
      return await loadMoreComplaintConversationMessages();
    }
    return await loadMoreConversationMessages();
  };

  const selectedConversation = useMemo(() => {
    if (selectedConversationId === null) {
      return null;
    }
    if (selfMedicationConversationIsSelected) {
      return activeSelfMedicationConversation;
    }
    if (complaintConversationIsSelected) {
      return complaintConversation;
    }
    return (
      sellerConversations.find((c) => c.props.id === selectedConversationId) ||
      null
    );
  }, [
    selectedConversationId,
    sellerConversations,
    activeSelfMedicationConversation,
    complaintConversation,
  ]);

  useEffect(() => {
    if (
      initialOpenedTab === EChatTab.Support &&
      initialOpenedTabTarget === EChatTabTarget.SelfMedication
    ) {
      setSelectedConversationId(
        activeSelfMedicationConversation?.props.id ?? null
      );
    }

    if (
      initialOpenedTab === EChatTab.Support &&
      initialOpenedTabTarget === EChatTabTarget.Complaint
    ) {
      setSelectedConversationId(complaintConversation?.props.id ?? null);
    }
  }, [
    initialOpenedTab,
    initialOpenedTabTarget,
    activeSelfMedicationConversation,
    complaintConversation,
  ]);

  useEffect(() => {
    if (selectedConversation === null) {
      return;
    }
    if (selectedConversation.props.recipient.props.branchId) {
      setSelectedBranchId(selectedConversation.props.recipient.props.branchId);
      return;
    }
    setSelectedBranchId(null);
  }, [selectedConversation]);

  // Set selected conversation to the branch conversation if branchId is
  // provided and there is a conversation for that branch
  useEffect(() => {
    if (!sellerConversations || !selectedBranchId) {
      return;
    }

    let conversationForBranch = null;
    if (selectedBranchId) {
      conversationForBranch = sellerConversations.find(
        (c) => c.props.recipient.props.branchId === selectedBranchId
      );
    }

    if (conversationForBranch) {
      setSelectedConversationId(conversationForBranch.props.id);
    }
  }, [selectedBranchId, sellerConversations]);

  return (
    <MarketplaceCustomerChatContext.Provider
      value={{
        currentConversationContact: getCurrentConversationContact(),
        isGettingCurrentConversationContact,
        selfMedicationConversation: activeSelfMedicationConversation,
        sellerConversations,
        complaintConversation: complaintConversation || null,
        selectedConversationId,
        setSelectedConversationId,
        selectedConversation,
        isGettingConversations,
        isGettingMoreConversations,
        isReachingEndConversations,
        loadMoreConversations,
        refreshConversations,
        isRefreshingConversations,
        currentConversationMessages: getCurrentConversationMessages(),
        isGettingCurrentConversationMessages,
        isGettingMoreCurrentConversationMessages,
        isReachingEndCurrentConversationMessages,
        addCurrentConversationMessages,
        loadMoreCurrentConversationMessages,
        createSelfMedicationConversation,
        isCreatingSelfMedicationConversation,
        currentOpenedTab,
        setCurrentOpenedTab,
      }}
    >
      {children}
    </MarketplaceCustomerChatContext.Provider>
  );
}
